/**
 * MODULE: domain/pages/frontend/utils/pageBrainFocusUtils.ts
 * PURPOSE: Resolve brain context focus from page CRM links (deal > contact > company > page).
 * DEPENDENCIES: pages.types
 * PUBLIC_API: resolvePageBrainFocus, pageBrainPanelTitle, pageBrainEmptyStateCopy
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95 / 150 lines
 * RECENT: 2026-05-25 [2.2.1195] — multi-entity focus priority (deal, contact, account, page).
 */

import type { Page } from '../../contracts/pages.types';

export type PageBrainFocusKind = 'deal' | 'contact' | 'account' | 'page';

/** Map assistant context entity_type to CRM brain focus kind [2.2.1251]. */
export function brainFocusKindFromContextEntity(
  entityType?: string | null,
): Extract<PageBrainFocusKind, 'deal' | 'contact' | 'account'> | null {
  const normalized = (entityType || '').trim().toLowerCase();
  if (normalized === 'deal' || normalized === 'opportunity') return 'deal';
  if (normalized === 'contact') return 'contact';
  if (normalized === 'account' || normalized === 'company') return 'account';
  return null;
}

export type PageBrainFocus = {
  kind: PageBrainFocusKind;
  /** Value sent to context resolve focus_entity_hint.query */
  query: string;
  /** CRM or page id — woven into resolve user_message for sharper brain matching */
  entityId: string;
  /** Human label for panel subtitle and empty states */
  label: string;
  enabled: boolean;
};

export function resolvePageBrainFocus(
  page: Pick<
    Page,
    | 'id'
    | 'title'
    | 'linked_deal_id'
    | 'linked_deal_label'
    | 'linked_contact_id'
    | 'linked_contact_label'
    | 'linked_company_id'
    | 'linked_company_label'
  >,
): PageBrainFocus | null {
  const pageId = (page.id || '').trim();
  if (!pageId) return null;

  const dealId = (page.linked_deal_id || '').trim();
  const dealLabel = (page.linked_deal_label || '').trim();
  if (dealId) {
    return {
      kind: 'deal',
      query: dealLabel || dealId,
      entityId: dealId,
      label: dealLabel || 'Linked deal',
      enabled: true,
    };
  }

  const contactId = (page.linked_contact_id || '').trim();
  const contactLabel = (page.linked_contact_label || '').trim();
  if (contactId) {
    return {
      kind: 'contact',
      query: contactLabel || contactId,
      entityId: contactId,
      label: contactLabel || 'Linked contact',
      enabled: true,
    };
  }

  const companyId = (page.linked_company_id || '').trim();
  const companyLabel = (page.linked_company_label || '').trim();
  if (companyId) {
    return {
      kind: 'account',
      query: companyLabel || companyId,
      entityId: companyId,
      label: companyLabel || 'Linked account',
      enabled: true,
    };
  }

  const title = (page.title || '').trim() || 'Untitled page';
  return {
    kind: 'page',
    query: title,
    entityId: pageId,
    label: title,
    enabled: true,
  };
}

export function pageBrainPanelTitle(kind: PageBrainFocusKind): string {
  switch (kind) {
    case 'deal':
      return 'Deal insights';
    case 'contact':
      return 'Contact insights';
    case 'account':
      return 'Account insights';
    default:
      return 'Page insights';
  }
}

export function pageBrainFocusSectionLabel(kind: PageBrainFocusKind): string {
  switch (kind) {
    case 'deal':
      return 'Deal focus';
    case 'contact':
      return 'Contact focus';
    case 'account':
      return 'Account focus';
    default:
      return 'Page focus';
  }
}

export function pageBrainEmptyStateCopy(kind: PageBrainFocusKind, label: string): string {
  const name = label.trim() || 'this record';
  switch (kind) {
    case 'page':
      return `No live insights for ${name} yet. Link a deal, contact, or account from the page header for richer context.`;
    case 'deal':
    case 'contact':
    case 'account':
      return `No live insights for ${name} yet. They will appear here as activity builds up.`;
    default:
      return 'Insights will appear here when the brain has enough context.';
  }
}

function focusEntityIdClause(entityId: string): string {
  const id = entityId.trim();
  return id ? ` [entity_id: ${id}]` : '';
}

/** User message for POST /assistant/context/resolve — includes stable entity id when known. */
export function pageBrainResolveUserMessage(
  kind: PageBrainFocusKind,
  label: string,
  query: string,
  entityId = '',
): string {
  const idClause = focusEntityIdClause(entityId);
  switch (kind) {
    case 'deal':
      return label
        ? `What should I focus on for ${label}?${idClause}`
        : `What should I focus on for this deal?${idClause}`;
    case 'contact':
      return label
        ? `What should I focus on for contact ${label}?${idClause}`
        : `What should I focus on for this contact?${idClause}`;
    case 'account':
      return label
        ? `What should I focus on for account ${label}?${idClause}`
        : `What should I focus on for this account?${idClause}`;
    default:
      return query
        ? `What context matters for workspace page "${query}"?${idClause}`
        : `What context matters for this workspace page?${idClause}`;
  }
}
